// In-memory persistence for PGA flags applied to import products.
// Mock-only — module-scoped state, resets on hot reload.

import type { PgaCode } from "./pga-schema";

export interface PgaFlagDocument {
  id: string;
  name: string;
  kind: string;
  size: number; // bytes
  uploadedAt: string;
}

export interface AppliedPgaFlag {
  code: PgaCode;
  addedAt: string;
  values: Record<string, unknown>;
  repeaters: Record<string, Record<string, unknown>[]>;
  documents: PgaFlagDocument[];
}

const store: Record<string, AppliedPgaFlag[]> = {};

// Seed the demo product with two partially-filled flags so completeness reads well.
store["ip-405"] = [
  {
    code: "FDA",
    addedAt: new Date(Date.now() - 5 * 86400_000).toISOString(),
    values: {
      "declare.declare": "Required",
      "declare.programCode": "DEV",
      "declare.processingCode": "REV",
      "productDetails.productName": "Catalytic converter substrate",
      "productDetails.hsCode": "8421.39.80",
      "productDetails.countryOfOrigin": "China",
    },
    repeaters: {
      entities: [{ role: "Manufacturer", name: "Jiangsu Catalyst Works", country: "China" }],
    },
    documents: [
      {
        id: "doc-fda-1",
        name: "FDA-Prior-Notice-2026-Q2.pdf",
        kind: "application/pdf",
        size: 184_320,
        uploadedAt: new Date(Date.now() - 4 * 86400_000).toISOString(),
      },
    ],
  },
  {
    code: "EPA_TSCA",
    addedAt: new Date(Date.now() - 2 * 86400_000).toISOString(),
    values: {
      "declare.tscaCertification": "Positive — complies with TSCA",
    },
    repeaters: {},
    documents: [],
  },
];

export function getAppliedFlags(productId: string): AppliedPgaFlag[] {
  return store[productId] ? [...store[productId]] : [];
}

// Replaces a product's flag list wholesale — used to hydrate the in-memory
// store from a real backend fetch (real products only) so the rest of this
// module's synchronous read/write API stays unchanged for every caller.
export function setAppliedFlags(productId: string, flags: AppliedPgaFlag[]): void {
  store[productId] = [...flags];
}

export function addAppliedFlag(
  productId: string,
  code: PgaCode,
  seedValues: Record<string, unknown> = {},
  seedRepeaters: Record<string, Record<string, unknown>[]> = {},
): AppliedPgaFlag {
  const list = store[productId] ?? (store[productId] = []);
  const existing = list.find((f) => f.code === code);
  if (existing) return existing;
  const flag: AppliedPgaFlag = {
    code,
    addedAt: new Date().toISOString(),
    values: seedValues,
    repeaters: seedRepeaters,
    documents: [],
  };
  list.push(flag);
  return flag;
}

export function updateAppliedFlag(
  productId: string,
  code: PgaCode,
  values: Record<string, unknown>,
  repeaters: Record<string, Record<string, unknown>[]>,
): void {
  const list = store[productId];
  if (!list) return;
  const f = list.find((x) => x.code === code);
  if (!f) return;
  f.values = values;
  f.repeaters = repeaters;
}

export function removeAppliedFlag(productId: string, code: PgaCode): void {
  const list = store[productId];
  if (!list) return;
  store[productId] = list.filter((f) => f.code !== code);
}

export function hasAppliedFlag(productId: string, code: PgaCode): boolean {
  return !!store[productId]?.some((f) => f.code === code);
}

export function addFlagDocument(
  productId: string,
  code: PgaCode,
  doc: Omit<PgaFlagDocument, "id" | "uploadedAt">,
): PgaFlagDocument | null {
  const list = store[productId];
  if (!list) return null;
  const f = list.find((x) => x.code === code);
  if (!f) return null;
  const record: PgaFlagDocument = {
    ...doc,
    id: `doc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    uploadedAt: new Date().toISOString(),
  };
  f.documents.push(record);
  return record;
}

export function removeFlagDocument(productId: string, code: PgaCode, docId: string): void {
  const list = store[productId];
  if (!list) return;
  const f = list.find((x) => x.code === code);
  if (!f) return;
  f.documents = f.documents.filter((d) => d.id !== docId);
}
